<script setup lang="ts">
const { t } = useTexts();
const { parameters } = useParameters();
const { showMoneyFormat } = useWallet();
const { realWallet } = storeToRefs(useWalletStore());

const minWithdrawAmountBrand = computed<number>(() => {
  return parameters.value?.min_withdraw_value || 100;
});

const minWithdrawAmountUser = computed<number>(() => {
  return realWallet.value.minWithdrawAmount;
});

const minWithdrawComputedValue = computed<number>(() => {
  return minWithdrawAmountUser.value
    ? minWithdrawAmountUser.value
    : minWithdrawAmountBrand.value;
});

const maxWithdrawAmountUser = computed<number>(() => {
  return realWallet.value.maxWithdrawAmount;
});

const maxWithdrawAmountBrand = computed<number>(() => {
  return parameters.value?.max_withdraw_value || 50000;
});

const maxWithdrawComputedValue = computed<number>(() => {
  return maxWithdrawAmountUser.value
    ? maxWithdrawAmountUser.value
    : maxWithdrawAmountBrand.value;
});

const minWithdrawValueFormatted = computed<string>(() => {
  return showMoneyFormat({ value: minWithdrawComputedValue.value / 100 });
});

const maxWithdrawValueFormatted = computed<string>(() => {
  return showMoneyFormat({ value: maxWithdrawComputedValue.value / 100 });
});

const isUnlimitedWithdrawPerDay = computed<boolean>(() => {
  return parameters.value?.withdrawals_interval_type === 'unlimited';
});

const timeBetweenWithdrawals = computed<number>(() => {
  return parameters.value?.time_between_withdrawals || 0;
});

const timeBetweenWithdrawalsTypeText = computed<string>(() => {
  const intervalType = parameters.value?.withdrawals_interval_type;

  const intervalTexts = {
    by_day: t(
      'gamification.tournament.timer.days_count',
      timeBetweenWithdrawals.value,
    ),
    by_hour: t(
      'gamification.tournament.timer.hours_count',
      timeBetweenWithdrawals.value,
    ),
    by_minute: t(
      'gamification.tournament.timer.minutes_count',
      timeBetweenWithdrawals.value,
    ),
    unlimited: t('account.wallet.withdraw_modal.rules.unlimited'),
  };

  return (
    intervalTexts[intervalType] ||
    t('account.wallet.withdraw_modal.rules.unlimited')
  );
});

const maxWithdrawPerDayFormatted = computed<string>(() => {
  const totalValue = parameters.value?.withdraw_daily_max_amount || 0;

  return showMoneyFormat({ value: totalValue / 100 });
});

const withdrawQuantityOnList = computed<string>(() => {
  if (isUnlimitedWithdrawPerDay.value) {
    return t('account.wallet.withdraw_modal.rules.unlimited');
  }

  return `${t('account.wallet.withdraw_modal.rules.limit_min_time')} ${timeBetweenWithdrawals.value} ${timeBetweenWithdrawalsTypeText.value}`;
});

const withdrawQuantityOnText = computed<string>(() => {
  if (isUnlimitedWithdrawPerDay.value) {
    return t('account.wallet.withdraw_modal.rules.unlimited_text');
  }

  return `${t('account.wallet.withdraw_modal.rules.withdraw_limit_min')} ${timeBetweenWithdrawals.value} ${timeBetweenWithdrawalsTypeText.value}`;
});
</script>

<template>
  <div class="rules-box p-6">
    <article class="rules-rows">
      <p>
        {{ $t('account.wallet.withdraw_modal.rules.min_withdraw') }}
        <span>{{ minWithdrawValueFormatted }}</span>
      </p>

      <p>
        {{ $t('account.wallet.withdraw_modal.rules.maximum_withdraw') }}
        <span>{{ maxWithdrawValueFormatted }}</span>
      </p>

      <p>
        {{ $t('account.wallet.withdraw_modal.rules.withdraw_quantity') }}
        <span>{{ withdrawQuantityOnList }}</span>
      </p>

      <p>
        {{ $t('account.wallet.withdraw_modal.rules.maximum_withdraw_per_day') }}
        <span>{{ maxWithdrawPerDayFormatted }}</span>
      </p>
    </article>
    <hr />
    <article :class="$style.rulesText">
      <div
        v-html="
          $t('account.wallet.withdraw_modal.rules.text', {
            min_withdraw: minWithdrawValueFormatted,
            max_withdraw: maxWithdrawValueFormatted,
            withdraw_quantity: withdrawQuantityOnText,
            withdraw_limit: maxWithdrawPerDayFormatted,
          })
        "
      >
      </div>
    </article>
  </div>
</template>

<style module lang="scss">
.rulesText {
  @apply text-texts text-xs;
}
</style>
